import { useCallback, useState } from "react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

const INITIAL_BATCH_WARNING = { show: false, item: null, batch: null };

export const useSalesCart = (t) => {
    const [cart, setCart] = useState([]);
    const [batchWarning, setBatchWarning] = useState(INITIAL_BATCH_WARNING);

    const resetBatchWarning = useCallback(() => {
        setBatchWarning(INITIAL_BATCH_WARNING);
    }, []);

    const removeItem = useCallback((itemId, batchId) => {
        setCart((prevCart) =>
            prevCart.filter((item) => !(item._id === itemId && item.selectedBatchId === batchId))
        );
    }, []);

    const upsertCartItem = useCallback(
        (product, batch, quantity = 1) => {
            if (!product || !batch) {
                return;
            }

            const safeQuantity = Math.max(1, Number(quantity) || 1);
            setCart((prevCart) => {
                const existingIndex = prevCart.findIndex(
                    (item) => item._id === product._id && item.selectedBatchId === batch._id
                );

                if (existingIndex !== -1) {
                    const existingItem = prevCart[existingIndex];
                    const maxQuantity = batch.totalRemainingMedicine;
                    const newQuantity = Math.min(existingItem.quantity + safeQuantity, maxQuantity);

                    if (existingItem.quantity === newQuantity) {
                        if (existingItem.quantity + safeQuantity > maxQuantity) {
                            toast.info(
                                t("Cannot add more {{name}}. Maximum available: {{qty}}", {
                                    name: product.brandName || product.saltName,
                                    qty: maxQuantity,
                                })
                            );
                        }
                        return prevCart;
                    }

                    const updatedCart = [...prevCart];
                    updatedCart[existingIndex] = {
                        ...existingItem,
                        quantity: newQuantity,
                        maxQuantity,
                        salePrice: batch.unitPricePerMedicine,
                        // Preserve tax percentage from batch if available
                        taxPercentage: batch.taxPercentage || existingItem.taxPercentage || 0
                    };
                    return updatedCart;
                }

                return [
                    ...prevCart,
                    {
                        ...product,
                        batchList: product.batchList || product.stockData || [],
                        selectedBatchId: batch._id,
                        batchNumber: batch.batchNumber,
                        expiryDate: batch.expiryDate,
                        salePrice: batch.unitPricePerMedicine,
                        quantity: Math.min(safeQuantity, batch.totalRemainingMedicine),
                        maxQuantity: batch.totalRemainingMedicine,
                        // Add tax percentage from batch
                        taxPercentage: batch.taxPercentage || 0,
                        // Add batch tax info for reference
                        batchTaxData: {
                            taxPercentage: batch.taxPercentage,
                            IGST: batch.IGST,
                            CGST: batch.CGST,
                            SGST: batch.SGST,
                            isIGST: batch.isIGST
                        }
                    },
                ];
            });
        },
        [t]
    );

    const addProductToCart = useCallback(
        (product) => {
            const batches = product?.stockData || [];

            if (!batches.length) {
                Swal.fire({
                    icon: "info",
                    title: t("No batches found"),
                    text: t(
                        "No stock batches available for {{name}}. Please add stock before selling.",
                        { name: product?.brandName }
                    ),
                    confirmButtonText: t("OK"),
                });
                return;
            }

            setCart((prevCart) => {
                let batchToUse = null;
                for (const batch of batches) {
                    const cartItem = prevCart.find(
                        (item) => item._id === product._id && item.selectedBatchId === batch._id
                    );
                    if (!cartItem || cartItem.quantity < batch.totalRemainingMedicine) {
                        batchToUse = batch;
                        break;
                    }
                }

                if (!batchToUse) {
                    Swal.fire({
                        icon: "info",
                        title: t("All batches at max"),
                        text: t("All batches for this product are already at max quantity in cart."),
                        confirmButtonText: t("OK"),
                    });
                    return prevCart;
                }

                const existingIndex = prevCart.findIndex(
                    (item) => item._id === product._id && item.selectedBatchId === batchToUse._id
                );

                if (existingIndex !== -1) {
                    const existingItem = prevCart[existingIndex];
                    if (existingItem.quantity < batchToUse.totalRemainingMedicine) {
                        const updatedCart = [...prevCart];
                        updatedCart[existingIndex] = {
                            ...existingItem,
                            quantity: existingItem.quantity + 1,
                            maxQuantity: batchToUse.totalRemainingMedicine,
                            salePrice: batchToUse.unitPricePerMedicine,
                            taxPercentage: batchToUse.taxPercentage || existingItem.taxPercentage || 0
                        };
                        return updatedCart;
                    }
                    setBatchWarning({ show: true, item: existingItem, batch: batchToUse });
                    return prevCart;
                }

                return [
                    ...prevCart,
                    {
                        ...product,
                        batchList: product.batchList || batches,
                        selectedBatchId: batchToUse._id,
                        batchNumber: batchToUse.batchNumber,
                        expiryDate: batchToUse.expiryDate,
                        salePrice: batchToUse.unitPricePerMedicine,
                        quantity: 1,
                        maxQuantity: batchToUse.totalRemainingMedicine,
                        // Add tax percentage from batch
                        taxPercentage: batchToUse.taxPercentage || 0,
                        // Add batch tax info
                        batchTaxData: {
                            taxPercentage: batchToUse.taxPercentage,
                            IGST: batchToUse.IGST,
                            CGST: batchToUse.CGST,
                            SGST: batchToUse.SGST,
                            isIGST: batchToUse.isIGST
                        }
                    },
                ];
            });
        },
        [t]
    );

    const handleBatchChange = useCallback((itemId, batchId) => {
        setCart((prevCart) =>
            prevCart.map((item) => {
                if (item._id === itemId) {
                    const batch = item.batchList?.find((b) => b._id === batchId);
                    if (!batch) {
                        return item;
                    }
                    return {
                        ...item,
                        selectedBatchId: batchId,
                        batchNumber: batch.batchNumber,
                        expiryDate: batch.expiryDate,
                        salePrice: batch.unitPricePerMedicine,
                        quantity: Math.min(1, batch.totalRemainingMedicine),
                        maxQuantity: batch.totalRemainingMedicine,
                        tax: batch.tax || 0,
                        // Update tax percentage from new batch
                        taxPercentage: batch.taxPercentage || 0,
                        // Update batch tax info
                        batchTaxData: {
                            taxPercentage: batch.taxPercentage,
                            IGST: batch.IGST,
                            CGST: batch.CGST,
                            SGST: batch.SGST,
                            isIGST: batch.isIGST
                        }
                    };
                }
                return item;
            })
        );
    }, []);

    const updateItemField = useCallback((itemId, batchId, field, value) => {
        setCart((prevCart) =>
            prevCart.map((item) =>
                item._id === itemId && item.selectedBatchId === batchId
                    ? { ...item, [field]: Number(value) }
                    : item
            )
        );
    }, []);

    const handleQuantityChange = useCallback((itemId, batchId, quantity) => {
        const numericQuantity = Number(quantity);
        if (!Number.isFinite(numericQuantity)) return;

        setCart((prevCart) =>
            prevCart.map((item) => {
                if (item._id === itemId && item.selectedBatchId === batchId) {
                    if (numericQuantity > item.maxQuantity) {
                        setBatchWarning({
                            show: true,
                            item,
                            batch: item.batchList?.find((b) => b._id === item.selectedBatchId),
                        });
                        return { ...item, quantity: item.maxQuantity };
                    }
                    return { ...item, quantity: Math.max(1, numericQuantity) };
                }
                return item;
            })
        );
    }, []);

    const addNewBatchEntry = useCallback(
        (item) => {
            const usedBatchIds = cart
                .filter((ci) => ci._id === item._id)
                .map((ci) => ci.selectedBatchId);

            const availableBatches = item.batchList?.filter(
                (batch) => !usedBatchIds.includes(batch._id) && batch.totalRemainingMedicine > 0
            );

            if (!availableBatches?.length) {
                Swal.fire({
                    icon: "info",
                    title: t("No more batches available"),
                    text: t(
                        "No more stock batches available for {{name}}. Please add stock before selling.",
                        { name: item.brandName }
                    ),
                    confirmButtonText: t("OK"),
                });
                resetBatchWarning();
                return;
            }

            const batch = availableBatches[0];
            setCart((prevCart) => [
                ...prevCart,
                {
                    ...item,
                    selectedBatchId: batch._id,
                    batchNumber: batch.batchNumber,
                    expiryDate: batch.expiryDate,
                    salePrice: batch.unitPricePerMedicine,
                    quantity: 1,
                    maxQuantity: batch.totalRemainingMedicine,
                    //   add tax amount
                    tax: batch.tax || 0,
                    // Add tax from batch
                    taxPercentage: batch.taxPercentage || 0,
                    batchTaxData: {
                        taxPercentage: batch.taxPercentage,
                        IGST: batch.IGST,
                        CGST: batch.CGST,
                        SGST: batch.SGST,
                        isIGST: batch.isIGST
                    }
                },
            ]);
            resetBatchWarning();
        },
        [cart, resetBatchWarning, t]
    );

    const resetCart = useCallback(() => {
        setCart([]);
        resetBatchWarning();
    }, [resetBatchWarning]);

    return {
        cart,
        setCart,
        batchWarning,
        setBatchWarning,
        resetBatchWarning,
        addProductToCart,
        upsertCartItem,
        handleBatchChange,
        updateItemField,
        handleQuantityChange,
        removeItem,
        addNewBatchEntry,
        resetCart,
    };
};